import { queryKeys } from "@/lib/query-keys";
import quoteService, {
  Quote,
  acceptQuote,
  counterQuote,
  createQuote,
  getQuotes,
  rejectQuote,
} from "@/lib/services/quotes";
import {
  QueryClient,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

// ── List ──────────────────────────────────────────────────────────────────────

export function useQuotes(
  params?: { booking_id?: number; status?: string },
  options?: Omit<
    UseQueryOptions<{ data: Quote[]; meta: any }>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: queryKeys.quotes.list(params),
    queryFn: () => getQuotes(params),
    ...options,
  });
}

type QuotesListCache = { data: Quote[]; meta?: unknown };

export function syncQuote(qc: QueryClient, quote: Quote): void {
  if (!quote?.booking_id) {
    return;
  }

  qc.setQueryData<QuotesListCache>(
    queryKeys.quotes.list({ booking_id: quote.booking_id }),
    (prev) => {
      const existing = prev?.data ?? [];
      const withoutDuplicate = existing.filter((q) => q.id !== quote.id);

      return {
        data: [quote, ...withoutDuplicate],
        meta: prev?.meta,
      };
    },
  );
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useAcceptQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => acceptQuote(id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
      qc.invalidateQueries({ queryKey: queryKeys.bookings.byBooking(updated.booking_id) });
    },
  });
}

export function useRejectQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => rejectQuote(id),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
      qc.invalidateQueries({ queryKey: queryKeys.bookings.byBooking(updated.booking_id) });
    },
  });
}

export function useCounterQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      amount,
      notes,
    }: {
      id: number;
      amount: number;
      notes?: string;
    }) => counterQuote(id, amount, notes),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
      qc.invalidateQueries({
        queryKey: queryKeys.bookings.byBooking(updated.booking_id),
      });
    },
  });
}

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createQuote.bind(quoteService),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
    },
  });
}
